import React from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

const MS_POR_DIA = 24 * 60 * 60 * 1000;
const UMBRAL_ALERTA_DIAS = 2;

const medianoche = (fecha) => {
  const resultado = new Date(fecha);
  resultado.setHours(0, 0, 0, 0);
  return resultado;
};

const sumarDias = (fecha, dias) => {
  const resultado = medianoche(fecha);
  resultado.setDate(resultado.getDate() + Math.round(dias));
  return resultado;
};

const diferenciaDias = (fechaObjetivo, fechaBase) =>
  Math.round((medianoche(fechaObjetivo) - medianoche(fechaBase)) / MS_POR_DIA);

// Clave local YYYY-MM-DD (evita el corrimiento de día que introduciría
// toISOString() al convertir a UTC en husos horarios positivos).
const claveFecha = (fecha) => {
  const y = fecha.getFullYear();
  const m = String(fecha.getMonth() + 1).padStart(2, "0");
  const d = String(fecha.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatearFecha = (fecha) =>
  fecha.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const nivelAlerta = (dias) => {
  if (dias === null || dias === undefined) return null;
  if (dias < 0) return "vencido";
  if (dias <= UMBRAL_ALERTA_DIAS) return "proximo";
  return "ok";
};

const CalendarioProyecto = ({ proyecto, pasoActivo, titulosPasos }) => {
  if (!proyecto || !proyecto.fechaCreacion) return null;

  const hoy = new Date();
  const totalPasos = titulosPasos.length;
  const duracionPorPaso = proyecto.duracionDias / totalPasos;

  const fechasPasos = titulosPasos.map((titulo, indice) => {
    const numero = indice + 1;
    return {
      numero,
      titulo,
      fecha: sumarDias(proyecto.fechaCreacion, duracionPorPaso * numero),
    };
  });

  const fechaLimiteProyecto = fechasPasos[fechasPasos.length - 1].fecha;
  const mapaFechasPasos = new Map(
    fechasPasos.map((p) => [claveFecha(p.fecha), p])
  );
  const claveFinal = claveFecha(fechaLimiteProyecto);
  const claveHoy = claveFecha(hoy);

  const pasoActivoInfo = fechasPasos.find((p) => p.numero === pasoActivo);
  const diasRestantesPasoActivo = pasoActivoInfo
    ? diferenciaDias(pasoActivoInfo.fecha, hoy)
    : null;
  const diasRestantesProyecto = diferenciaDias(fechaLimiteProyecto, hoy);
  const nivelPasoActivo = nivelAlerta(diasRestantesPasoActivo);

  return (
    <aside className="docgen-calendario">
      <div className="docgen-calendario-header">
        <span className="docgen-calendario-icono" aria-hidden="true">
          📅
        </span>
        Fechas límite
      </div>

      {nivelPasoActivo && nivelPasoActivo !== "ok" && (
        <div className={`docgen-alerta docgen-alerta--${nivelPasoActivo}`}>
          {nivelPasoActivo === "vencido"
            ? `⚠ El paso "${pasoActivoInfo.titulo}" venció hace ${Math.abs(
                diasRestantesPasoActivo
              )} día(s).`
            : `⏳ El paso "${pasoActivoInfo.titulo}" vence en ${diasRestantesPasoActivo} día(s).`}
        </div>
      )}

      <Calendar
        locale="es-ES"
        next2Label={null}
        prev2Label={null}
        defaultActiveStartDate={pasoActivoInfo ? pasoActivoInfo.fecha : hoy}
        tileClassName={({ date, view }) => {
          if (view !== "month") return null;
          const clave = claveFecha(date);
          const clases = [];
          if (clave === claveHoy) clases.push("docgen-calendario-tile--hoy");
          if (mapaFechasPasos.has(clave)) {
            clases.push("docgen-calendario-tile--paso");
            if (clave === claveFinal) clases.push("docgen-calendario-tile--final");
          }
          return clases.length ? clases.join(" ") : null;
        }}
        tileContent={({ date, view }) => {
          if (view !== "month") return null;
          const paso = mapaFechasPasos.get(claveFecha(date));
          return paso ? (
            <span className="docgen-calendario-marca">P{paso.numero}</span>
          ) : null;
        }}
      />

      <ul className="docgen-calendario-lista">
        {fechasPasos.map((p) => {
          const dias = diferenciaDias(p.fecha, hoy);
          const nivel = nivelAlerta(dias);
          return (
            <li
              key={p.numero}
              className={
                "docgen-calendario-item" +
                (p.numero === pasoActivo ? " docgen-calendario-item--activo" : "")
              }
            >
              <span className="docgen-calendario-item-numero">P{p.numero}</span>
              <span className="docgen-calendario-item-titulo">{p.titulo}</span>
              <span
                className={`docgen-calendario-item-fecha docgen-calendario-item-fecha--${nivel}`}
              >
                {formatearFecha(p.fecha)}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="docgen-calendario-resumen">
        Fecha límite del proyecto: <strong>{formatearFecha(fechaLimiteProyecto)}</strong>
        <br />
        {diasRestantesProyecto >= 0
          ? `${diasRestantesProyecto} día(s) restantes`
          : `Vencido hace ${Math.abs(diasRestantesProyecto)} día(s)`}
      </div>
    </aside>
  );
};

export default CalendarioProyecto;
